import React, { useEffect, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { NS, U2101ROLES } from "../../../../U2101";
import { U2000IdCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { U2000IdHeaderCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import type { U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import U2101LogicalUnitActionEditDlg from "./U2101LogicalUnitActionEditDlg";
import { emptyLogicalUnitAction, type LogicalUnitAction } from "../../../../U2101Service";
import U2101LogicalUnitActionDeleteDlg from "./U2101LogicalUnitActionDeleteDlg";
import type { U2101LogicalUnitFormExtendedHandlers } from "../../../U2101LogicalUnitForm";
import { u2101LogicalUnitActionHandler } from "./U2101LogicalUnitActionHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers, type U2000FormStateHandlersReturn } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { LOGICAL_UNIT_TYPES, type LogicalUnitType } from "../../../common/logicalUnitTypes";

export interface U2101LogicalUnitActionFormExtendedHandlers extends U2000FormStateHandlersReturn<LogicalUnitAction> {}

// Typage des props du composant
interface U2101LogicalUnitActionsComponentProps {
  u2101LogicalUnitformStateExtendedHandlers: U2101LogicalUnitFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "id",
      label: "",
      widthClass: "w-[50px]",
      type: "number",
      align: "center",
      headerDisplay: (column: any, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
        return <U2000IdHeaderCheckedBoxDisplay column={column} label={label} disabled={false} allRowSelected={allRowSelected} handleSelectAllRow={handleSelectAllRow} />;
      },
      display: (column: any, value: any, row: any, allowRowSelection: boolean, rowUpdated?: (row: any) => void) => {
        return <U2000IdCheckedBoxDisplay column={column} value={value} row={row} disabled={false} rowUpdated={rowUpdated} allowRowSelection={allowRowSelection} />;
      },
      isCriteriaField: false,
    },
    {
      name: "logicalUnitActionCode",
      label: t("lblLogicalUnitActionCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitActionDescription",
      label: t("lblLogicalUnitActionDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitActionTypes",
      label: t("lblLogicalUnitActionTypes", { ns: NS }),
      widthClass: "w-[20%]",
      type: "string",
      align: "left",
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      display: (_column: any, value: any, _row: any) => {
        // value peut être un tableau de valeurs (ex: ["admin", "organizational"])
        const lang = (t as any)?.i18n?.language || "fr";
        const getLabel = (val: string) => {
          const found = LOGICAL_UNIT_TYPES.find((type: LogicalUnitType) => type.value === val);
          if (!found) return val;
          return typeof found.label === "object" ? found.label[lang as keyof typeof found.label] : found.label;
        };
        if (Array.isArray(value)) {
          return (
            <div className="flex h-full items-center justify-start gap-1">
              {value.map((val, idx) => (
                <span key={idx} className={idx % 2 === 0 ? "rounded-sm bg-blue-100 px-1 text-blue-800" : "rounded-sm bg-red-100 px-1 text-red-800"}>
                  {getLabel(val)}
                </span>
              ))}
            </div>
          );
        }
        return <span className="rounded-sm bg-blue-100 px-1 text-blue-800">{getLabel(value as string)}</span>;
      },
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U2101LogicalUnitActionsBody: React.FC<U2101LogicalUnitActionsComponentProps> = ({ u2101LogicalUnitformStateExtendedHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<LogicalUnitAction>();

  const validateLogicalUnitAction = (logicalUnitAction: LogicalUnitAction) => {
    const result: Record<string, string> = {};
    if (!logicalUnitAction.logicalUnitActionCode) result.logicalUnitActionCode = t("errLogicalUnitActionCodeRequired", { ns: NS });
    if (!logicalUnitAction.logicalUnitActionDescription) {
      result.logicalUnitActionDescription = t("errLogicalUnitActionDescriptionRequired", { ns: NS });
    }

    // no duplicate Logical Unit Actions (by logicalUnitActionCode)
    const duplicate = gridStateHandlers.getData().find((item) => item.logicalUnitActionCode === logicalUnitAction.logicalUnitActionCode && item.id !== logicalUnitAction.id);
    if (duplicate) {
      result.logicalUnitActionCode = t("errLogicalUnitActionDuplicate", { ns: NS });
    }

    // At least one Logical Unit Action Type must be selected
    const logicalUnitActionTypes = (logicalUnitAction as any).logicalUnitActionTypes as unknown as string[];
    if (Array.isArray(logicalUnitActionTypes) && (logicalUnitActionTypes as any[]).length === 0) {
      result.logicalUnitActionTypes = t("errlogicalUnitActionTypeMissing", { ns: NS, codeAction: logicalUnitAction.logicalUnitActionCode });
    }

    return result;
  };

  const u2101LogicalUnitActionBL: U2000FormSBusinessHandlers<LogicalUnitAction> = {
    newRecord: () => {
      return emptyLogicalUnitAction;
    },

    // validation du formulaire
    validateForm: validateLogicalUnitAction,
  };

  const u2101LogicalUnitActionformStateHandlers = useU2000FormStateHandlers<LogicalUnitAction>({
    formState,
    formStateDispatch,
    businessHandlers: u2101LogicalUnitActionBL,
  });

  const u2101LogicalUnitActionHandlers = u2101LogicalUnitActionHandler({ gridStateHandlers, t, formStateHandlers: u2101LogicalUnitActionformStateHandlers });

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));

    // Initialiser les données du tableau
    const logicalUnitActions = u2101LogicalUnitformStateExtendedHandlers.getData?.()?.logicalUnitActions;
    gridStateHandlers.setData(Array.isArray(logicalUnitActions) ? logicalUnitActions : []);
    gridStateHandlers.setTotalRows(Array.isArray(logicalUnitActions) ? logicalUnitActions.length : 0);

    // Gérer les erreurs spécifiques au champ LogicalUnitActions
    const errorsField = u2101LogicalUnitformStateExtendedHandlers.getOtherErrors()?.logicalUnitActions;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u2101LogicalUnitformStateExtendedHandlers, t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u2101LogicalUnitActionHandlers.dataChangedEvent === 0) return;

    const logicalUnitActions = gridStateHandlers.getData();
    u2101LogicalUnitformStateExtendedHandlers.setData({
      ...u2101LogicalUnitformStateExtendedHandlers.getData?.(),
      logicalUnitActions: logicalUnitActions,
    });
  }, [u2101LogicalUnitActionHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2101LogicalUnitformStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2101LogicalUnitformStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false;
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u2101LogicalUnitActionHandlers.onAdd,
        hasRole: hasRole(U2101ROLES.ACTIONS),
        disabled: u2101LogicalUnitformStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u2101LogicalUnitActionHandlers.onModify,
        hasRole: hasRole(U2101ROLES.ACTIONS),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u2101LogicalUnitActionHandlers.onDelete,
        hasRole: hasRole(U2101ROLES.ACTIONS),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u2101LogicalUnitActionHandlers]
  );

  // Render
  return (
    <>
      <U2101LogicalUnitActionEditDlg u2101LogicalUnitActionHandler={u2101LogicalUnitActionHandlers} t={t} NS={NS} />
      <U2101LogicalUnitActionDeleteDlg u2101LogicalUnitActionHandler={u2101LogicalUnitActionHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u2101LogicalUnitformStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblLogicalUnitAction", {
          count: u2101LogicalUnitActionHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2101LogicalUnitActionsBody;
